import React, { useEffect, useState } from 'react'

import { faAngleDown, faAngleUp } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Field, Formik } from 'formik'
import linq from 'linq'
import NumberFormat from 'react-number-format'

import { SortingType } from './ViewTradelinesModal'
import { creditLoanTypeOptions } from './applicationSlice'
import Spinner from '@/components/Spinner'
import { Tradeline } from '@/types/unlock'

const TradelineGroupRow = ({
  group,
  application,
  sortConfig,
  updateTradeline,
  isUpdatingTradeline,
}) => {
  const [showChildren, setShowChildren] = useState(false)
  const [isPayoffChecked, setIsPayoffChecked] = useState(false)
  const [isSubjPropChecked, setIsSubjPropChecked] = useState(false)

  const sortedRows = React.useMemo(() => {
    const toBeSorted = group?.tradelines
    let sorted = linq.from(toBeSorted).orderBy(() => 1)

    sortConfig.forEach((stConfig) => {
      const customCompare = (fst, scd) => {
        const fstParsed = stConfig.parseNumber ? parseFloat(fst) : fst
        const scdParsed = stConfig.parseNumber ? parseFloat(scd) : scd

        if (stConfig.sortType === SortingType.Ascending) {
          return fstParsed < scdParsed ? -1 : 1
        }

        return fstParsed < scdParsed ? 1 : -1
      }

      sorted = sorted
        .thenBy((dataRow) => (dataRow[stConfig.propertyName] === null ? -1 : 1))
        .thenBy((dataRow) => dataRow[stConfig.propertyName], customCompare)
    })

    return sorted.toArray()
  }, [sortConfig, group])

  const hasIgnoredTradelines = React.useMemo(
    () => sortedRows.some((row: Tradeline) => row.ignore_indicator),
    [sortedRows]
  )

  const hasSubortinatedTradelines = React.useMemo(
    () => sortedRows.some((row: Tradeline) => row.is_subordinated),
    [sortedRows]
  )

  useEffect(() => {
    if (group?.tradelines?.every((tradeline) => tradeline.is_payoff && tradeline.payoff_included)) {
      setIsPayoffChecked(true)
    } else {
      setIsPayoffChecked(false)
    }

    if (group?.tradelines?.every((tradeline) => tradeline.is_subject_property)) {
      setIsSubjPropChecked(true)
    } else {
      setIsSubjPropChecked(false)
    }
  }, [group?.tradelines])

  const handleCheckIsPayoff = async () => {
    const checkState = !isPayoffChecked
    for (const tradeline of group?.tradelines) {
      await updateTradeline({
        external_id: tradeline.external_id,
        is_payoff: checkState,
        payoff_included: checkState,
      }).unwrap()
    }
  }

  const handleCheckIsSubjProp = async () => {
    const checkState = !isSubjPropChecked
    for (const tradeline of group?.tradelines) {
      await updateTradeline({
        external_id: tradeline.external_id,
        is_subject_property: checkState,
      }).unwrap()
    }
  }

  const isMorgageOrHelocOrSolar =
    group?.account_type == 'mortgage' ||
    group?.account_type == 'heloc' ||
    group?.account_type == 'solar'

  const getPayoffAmount = (tradeline: Partial<Tradeline>): string => {
    if (tradeline.is_payoff) {
      if (Number(tradeline.payoff_amount) > 0) {
        return tradeline.payoff_amount
      }
      return tradeline.unpaid_balance_amount
        ? parseFloat(`${tradeline.unpaid_balance_amount}`).toFixed(2)
        : '0.00'
    }
    return '0.00'
  }

  return (
    <>
      <tr
        className={`${showChildren ? 'bg-sky-200/20 border-t' : ''} text-xs`}
        onClick={() => setShowChildren((showChildren) => !showChildren)}
      >
        <td
          className={`capitalize flex items-center gap-1 px-6 py-3 text-center ${
            isMorgageOrHelocOrSolar ? 'text-error' : ''
          }`}
        >
          <div className='w-3'>
            <FontAwesomeIcon
              icon={showChildren ? faAngleUp : faAngleDown}
              width={10}
              color='black'
            />
          </div>
          {group.account_type}
        </td>
        <td></td> {/* Credit Loan Type */}
        <td></td> {/* Creditor */}
        <td className='px-6 py-3 text-center'>
          <NumberFormat
            className='border-none bg-transparent p-0 text-xs text-center w-full'
            value={group.unpaid_balance_amount}
            prefix='$'
            thousandSeparator
            readOnly={true}
            disabled={true}
            decimalScale={2}
            fixedDecimalScale
          />
        </td>
        <td className='px-6 py-3 max-w-[130px]'>
          <NumberFormat
            className='border-none bg-transparent p-0 text-xs text-center w-full'
            value={group.monthly_payment_amount}
            prefix='$'
            thousandSeparator
            readOnly={true}
            disabled={true}
            decimalScale={2}
            fixedDecimalScale
          />
        </td>
        <td className='px-6 py-3 max-w-[90px]'>
          <NumberFormat
            className='border-none bg-transparent p-0 text-xs text-center w-full'
            value={group.pmt_ratio}
            prefix=''
            suffix='%'
            decimalScale={1}
            thousandSeparator
            readOnly={true}
            disabled={true}
          />
        </td>
        <td className='px-6 py-3 text-center'>
          {isMorgageOrHelocOrSolar && (
            <>
              {!isUpdatingTradeline ? (
                <input
                  name='is_subject_property'
                  className='border border-sky-400 rounded p-2 disabled:bg-gray-400 cursor-pointer'
                  type='checkbox'
                  checked={isSubjPropChecked}
                  onChange={handleCheckIsSubjProp}
                  disabled={hasIgnoredTradelines}
                  readOnly={hasIgnoredTradelines || hasSubortinatedTradelines}
                />
              ) : (
                <Spinner size={4} />
              )}
            </>
          )}
        </td>
        <td className='px-6 py-3 text-center'>
          {!isUpdatingTradeline ? (
            <input
              name='is_payoff'
              className='border border-sky-400 rounded p-2 disabled:bg-gray-400 cursor-pointer'
              type='checkbox'
              checked={isPayoffChecked}
              onChange={handleCheckIsPayoff}
              disabled={hasIgnoredTradelines || hasSubortinatedTradelines}
              readOnly={hasIgnoredTradelines || hasSubortinatedTradelines}
            />
          ) : (
            <Spinner size={4} />
          )}
        </td>
        <td className='px-6 py-3 text-center'>{isUpdatingTradeline && <Spinner size={4} />}</td>
        <td className='px-6 py-3 text-center'>{isUpdatingTradeline && <Spinner size={4} />}</td>
      </tr>
      {showChildren &&
        sortedRows?.map((tradeline: Tradeline) => (
          <Formik
            key={tradeline.id}
            initialValues={tradeline}
            enableReinitialize
            onSubmit={async (values: Partial<Tradeline>) => {
              const newValues: Partial<Tradeline> = {
                external_id: tradeline.external_id,
                is_subject_property: values.is_subject_property,
                mortgage_for: values.is_subject_property
                  ? application.property?.id
                  : tradeline.mortgage_for,
                is_payoff: values.is_payoff,
                payoff_included: values.is_payoff,
                payoff_amount: getPayoffAmount(values),
                ignore_indicator: values.ignore_indicator,
                is_subordinated: values.is_subordinated,
              }
              delete newValues.pmt_ratio

              await updateTradeline(newValues).unwrap()
            }}
          >
            {({ values, submitForm, isSubmitting, setFieldValue }) => (
              <tr
                key={tradeline.id}
                className='text-xs bg-sky-200/20'
              >
                <td
                  className={`px-6 py-3 text-center ${isMorgageOrHelocOrSolar ? 'text-error' : ''}`}
                >
                  {values.account_type || '~'}
                  {values.manual_update_indicator && (
                    <>
                      <br />
                      <b>(Added)</b>
                    </>
                  )}
                </td>
                <td className={'px-6 py-3 text-center break-words'}>
                  {
                    creditLoanTypeOptions.find((option) => option.value === values.credit_loan_type)
                      ?.label
                  }
                </td>
                <td className='px-6 py-3 max-w-[160px] break-words'>
                  {values.name}
                  {values.solar_terms_in_months && (
                    <>
                      <br />
                      <b>{values.solar_terms_in_months} months |</b>
                    </>
                  )}
                </td>
                <td className='px-6 py-3 max-w-[140px]'>
                  <NumberFormat
                    className='border-none bg-transparent p-0 text-xs text-center w-full'
                    value={values.unpaid_balance_amount}
                    prefix='$'
                    thousandSeparator
                    readOnly={true}
                    disabled={true}
                    decimalScale={2}
                  />
                </td>
                <td className='px-6 py-3 max-w-[130px]'>
                  <NumberFormat
                    className='border-none bg-transparent p-0 text-xs text-center w-full'
                    value={values.monthly_payment_amount}
                    prefix='$'
                    thousandSeparator
                    readOnly={true}
                    disabled={true}
                    decimalScale={2}
                  />
                </td>
                <td className='px-6 py-3 max-w-[90px]'>
                  <NumberFormat
                    className='border-none bg-transparent p-0 text-xs text-center w-full'
                    value={values.pmt_ratio * 100}
                    prefix=''
                    suffix='%'
                    decimalScale={1}
                    thousandSeparator
                    readOnly={true}
                    disabled={true}
                  />
                </td>
                <td className='px-6 py-3 text-center'>
                  {isMorgageOrHelocOrSolar && (
                    <Field
                      name='is_subject_property'
                      checked={values.is_subject_property}
                      className='border border-sky-400 rounded p-2 disabled:bg-gray-400 cursor-pointer'
                      type='checkbox'
                      disabled={isUpdatingTradeline || values.ignore_indicator}
                      readOnly={isSubmitting || values.is_subordinated}
                      onChange={(e) => {
                        if (values.is_subordinated) return

                        const boolValue = e.target.value !== 'true'
                        setFieldValue('is_subject_property', boolValue)
                        submitForm()
                      }}
                    />
                  )}
                </td>
                <td className='px-6 py-3 text-center'>
                  <Field
                    name='is_payoff'
                    checked={values.is_payoff && values.payoff_included}
                    className='border border-sky-400 rounded p-2 disabled:bg-gray-400 cursor-pointer'
                    type='checkbox'
                    readOnly={isSubmitting || isUpdatingTradeline}
                    disabled={
                      isUpdatingTradeline || values.ignore_indicator || values.is_subordinated
                    }
                    onChange={(e) => {
                      const boolValue = !(values?.is_payoff && values?.payoff_included)
                      setFieldValue('is_payoff', boolValue)
                      setFieldValue('payoff_included', boolValue)
                      submitForm()
                    }}
                  />
                </td>
                <td className='px-6 py-3 text-center'>
                  <Field
                    name='is_subordinated'
                    checked={values.is_subordinated}
                    className='border border-sky-400 rounded p-2 disabled:bg-gray-400 cursor-pointer'
                    type='checkbox'
                    readOnly={isSubmitting || isUpdatingTradeline}
                    disabled={isUpdatingTradeline || values.ignore_indicator || values.is_payoff}
                    onChange={(e) => {
                      const boolValue = e.target.value !== 'true'
                      setFieldValue('is_subordinated', boolValue)
                      if (boolValue) {
                        setFieldValue('is_payoff', false)
                        setFieldValue('payoff_included', false)
                        setFieldValue('is_subject_property', true)
                      }
                      submitForm()
                    }}
                  />
                </td>
                <td className='px-6 py-3 text-center'>
                  <Field
                    name='ignore_indicator'
                    checked={values.ignore_indicator}
                    className='border border-sky-400 rounded p-2 disabled:bg-gray-400 cursor-pointer'
                    type='checkbox'
                    readOnly={isSubmitting || isUpdatingTradeline}
                    disabled={isUpdatingTradeline}
                    onChange={(e) => {
                      const boolValue = e.target.value !== 'true'
                      if (boolValue) {
                        setFieldValue('is_subject_property', false)
                        setFieldValue('is_payoff', false)
                        setFieldValue('payoff_included', false)
                        setFieldValue('is_subordinated', false)
                      }
                      setFieldValue('ignore_indicator', boolValue)
                      submitForm()
                    }}
                  />
                </td>
              </tr>
            )}
          </Formik>
        ))}
    </>
  )
}

export default TradelineGroupRow
